import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import { ArrowRight, Compass, Heart, Sparkles, Users } from 'lucide-react';

const VALUES = [
  { icon: Compass, title: 'Qualität statt Masse', desc: 'Wir sind überzeugt: Lieber 30 hochwertige Leads als 3.000 Adressen. Unsere KI ist auf Passung optimiert – nicht auf Volumen.' },
  { icon: Sparkles, title: 'Datenbasierte Entscheidungen', desc: 'Jeder Lead bekommt einen nachvollziehbaren Score mit Begründung. Vertrieb wird dadurch messbar – und planbar.' },
  { icon: Heart, title: 'Vertrieb mit Anstand', desc: 'DSGVO-konform, Opt-Out-Management und transparente Datenherkunft. Wir liefern Leads, mit denen man auch in 5 Jahren noch arbeiten kann.' },
  { icon: Users, title: 'Auf Augenhöhe', desc: 'Wir sind Mittelstand für Mittelstand. Keine Hotline-Schleifen, keine Account-Manager-Kette – Sie sprechen direkt mit denen, die das Produkt bauen.' },
];

export default function UeberUnsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="ueber-uns-page">
      <SEO
        title="Über uns – LaizyLeads | Hochwertige B2B Leadgenerierung made in Germany"
        description="Wir bauen LaizyLeads, weil B2B-Vertrieb mehr verdient hat als Massenkontakte. Mission, Werte und Hintergrund hinter dem Leadgenerator für hochwertige B2B Leads."
        path="/ueber-uns"
        keywords="LaizyLeads Mission, B2B Leadgenerierung Deutschland, hochwertige Leads, Leadgenerator made in Germany"
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium">
            <Compass className="w-3.5 h-3.5 mr-1.5" /> Über uns
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Wir bauen den Leadgenerator,<span className="block text-emerald-600">den wir selbst gebraucht hätten.</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            LaizyLeads entstand aus jahrelanger Frustration mit klassischer B2B-Leadgenerierung – veraltete Adresslisten, ungeprüfte Daten, unpersönliche Massenmails. Wir wussten: Es muss eine bessere Art geben, neue Kunden zu gewinnen.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl font-bold tracking-tight mb-5">Unsere Mission</h2>
          <p className="text-gray-700 leading-relaxed mb-4 text-lg">
            B2B-Vertrieb scheitert selten an Fleiß – meistens an der Datengrundlage. Vertriebsteams verschwenden Stunden mit der Recherche von Unternehmen, die am Ende doch nicht passen. Genau hier setzen wir an.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Unsere Mission ist es, B2B-Leadgenerierung qualitativ auf ein neues Niveau zu heben: <strong>Bessere Leads statt Massenkontakte.</strong> Wir wollen, dass Ihr Sales-Team morgens ins Büro kommt und genau weiß, mit welchen drei Unternehmen es heute spricht – und warum ausgerechnet mit denen.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Dafür kombinieren wir aktuelle Web-Daten, KI-Matching und ein durchdachtes Vertriebs-UX. Das Ergebnis ist eine moderne Leadgenerierung-Software, die nicht mehr Adressen liefert – sondern <em>bessere</em> Adressen.
          </p>
        </div>
      </section>

      {/* HINTERGRUND */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl font-bold tracking-tight mb-5">Hintergrund</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            LaizyLeads ist ein Produkt der <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">Laizyones</a> – einem inhabergeführten Unternehmen aus Sachsen-Anhalt. Wir kommen selbst aus dem Performance-Marketing- und SaaS-Umfeld und wissen, wie viel ein gutes erstes Gespräch wert ist – und wie viel ein schlechtes kostet.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Anstatt erneut eine weitere Adressliste zu bauen, haben wir uns für einen anderen Weg entschieden: KI-gestützte, datenbasierte Leadgenerierung mit Fokus auf Qualität, Erklärbarkeit und Compliance. Unser Standort: Wellen, Sachsen-Anhalt – Made in Germany, mit europäischen Datenschutz-Standards.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Heute arbeiten Vertriebsteams aus Agenturen, SaaS-Unternehmen, Industrie und Dienstleistung mit LaizyLeads – und finden Kunden, die sie vorher nicht auf dem Schirm hatten.
          </p>
        </div>
      </section>

      {/* WERTE */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Unsere Werte</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Was uns leitet</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Diese vier Prinzipien stehen hinter jeder Produktentscheidung – vom Algorithmus bis zur Preisgestaltung.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="p-7 rounded-2xl border border-gray-200 bg-gray-50">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{v.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl font-bold tracking-tight mb-6">Mehr erfahren</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/leistungen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Leistungen</h3>
              <p className="text-sm text-gray-500">Was unser Leadgenerator kann.</p>
            </Link>
            <Link to="/case-studies" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Case Studies</h3>
              <p className="text-sm text-gray-500">Echte Ergebnisse aus B2B-Teams.</p>
            </Link>
            <Link to="/kontakt" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Kontakt</h3>
              <p className="text-sm text-gray-500">Lassen Sie uns sprechen.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Werden Sie Teil unserer Vertriebsrevolution.</h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
            Testen Sie LaizyLeads kostenlos und überzeugen Sie sich selbst von hochwertigen B2B-Leads.
          </p>
          <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold" onClick={() => navigate('/register')}>
            Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
